import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  ListSubheader,
  Menu,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import {
  createEmailTemplate,
  updateEmailTemplate,
  CommunicationTemplateType,
  CommunicationTemplateVisibility,
  IEmailTemplate,
  IEmailTemplateInput,
  IEmailTemplateMeta,
  IEmailTemplateVariable,
  aiGenerateTemplateBody,
  AITone,
  AILength,
} from "../../../Api/emailTemplatesApi";
import { useNotifier } from "../../../Core/Notifier";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import AIAssistantPanel from "./AIPanel";
import { renderWithSampleContext, smsSegments } from "./previewUtils";

interface Props {
  open: boolean;
  meta: IEmailTemplateMeta;
  initialRow: IEmailTemplate | null;
  initialChannel: CommunicationTemplateType;
  onClose: () => void;
  onSaved: () => void;
}

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44,
    borderRadius: "10px",
    bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": { borderColor: B13.primary, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink },
} as const;

const EditorDrawer: React.FC<Props> = ({ open, meta, initialRow, initialChannel, onClose, onSaved }) => {
  const { showErrorMessage, showMessage } = useNotifier();

  // ── form state ────────────────────────────────────────────────────
  const [name, setName]           = useState(initialRow?.name ?? "");
  const [slug, setSlug]           = useState(initialRow?.slug ?? "");
  const [channel, setChannel]     = useState<CommunicationTemplateType>(initialRow?.template_type ?? initialChannel);
  const [category, setCategory]   = useState<string>(initialRow?.category ?? "");
  const [visibility, setVisibility] = useState<CommunicationTemplateVisibility>(
    initialRow?.visibility ?? meta.default_visibility_by_type[initialChannel] ?? "customer_facing"
  );
  const [subject, setSubject]     = useState(initialRow?.subject ?? "");
  const [description, setDescription] = useState(initialRow?.description ?? "");
  const [body, setBody]           = useState(initialRow?.body ?? "");
  const [isActive, setIsActive]   = useState(initialRow?.is_active ?? true);

  // Reset visibility default only when channel actually changes (not on
  // mount for an existing row).
  useEffect(() => {
    if (!initialRow) {
      setVisibility(meta.default_visibility_by_type[channel] ?? "customer_facing");
    }
  }, [channel, meta, initialRow]);

  const [saving, setSaving]       = useState(false);
  const [aiOpen, setAiOpen]       = useState(false);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile" | "sms">(
    channel === "sms" ? "sms" : "desktop"
  );

  // Insert-variable menu — anchored to whichever button was clicked.
  const [varMenu, setVarMenu] = useState<HTMLElement | null>(null);

  // For placing the cursor when we insert a variable into subject/body.
  const bodyRef    = useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const insertTargetRef = useRef<"body" | "subject">("body");

  const categoryOptions = useMemo(
    () => meta.categories_by_type[channel] ?? meta.categories,
    [meta, channel]
  );

  // Variables come pre-grouped in meta.variables — dedupe and group by
  // .group so the Insert menu is scannable.
  const groupedVars = useMemo(() => {
    const g: Record<string, IEmailTemplateVariable[]> = {};
    for (const v of meta.variables) {
      (g[v.group] = g[v.group] || []).push(v);
    }
    return g;
  }, [meta.variables]);

  const insertVariable = (key: string) => {
    setVarMenu(null);
    const token = `{{${key}}}`;
    const target = insertTargetRef.current;
    if (target === "subject") {
      const el = subjectRef.current;
      if (!el) { setSubject((s) => s + token); return; }
      const start = el.selectionStart ?? subject.length;
      const end = el.selectionEnd ?? subject.length;
      const next = subject.slice(0, start) + token + subject.slice(end);
      setSubject(next);
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
    } else {
      const el = bodyRef.current;
      if (!el) { setBody((b) => b + token); return; }
      const start = el.selectionStart ?? body.length;
      const end = el.selectionEnd ?? body.length;
      const next = body.slice(0, start) + token + body.slice(end);
      setBody(next);
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
    }
  };

  const openVarMenuFor = (anchor: HTMLElement, target: "body" | "subject") => {
    insertTargetRef.current = target;
    setVarMenu(anchor);
  };

  const handleAIInsert = (aiBody: string, replace: boolean) => {
    if (!replace && body.trim()) {
      setBody(`${body}\n\n${aiBody}`);
    } else {
      setBody(aiBody);
    }
    setAiOpen(false);
  };

  const canSave = name.trim().length > 0 && body.trim().length > 0
    && (channel !== "email" || subject.trim().length > 0);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const payload: IEmailTemplateInput = {
        name: name.trim(),
        slug: slug.trim() || undefined,
        subject: channel === "email" ? subject.trim() : undefined,
        description: description.trim() || undefined,
        body,
        category: category || undefined,
        template_type: channel,
        visibility,
        is_active: isActive,
      };
      if (initialRow) {
        await updateEmailTemplate(initialRow.id, payload);
        showMessage("Template updated.");
      } else {
        await createEmailTemplate(payload);
        showMessage("Template created.");
      }
      onSaved();
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  // Live preview — always renders against sample data so the preview
  // pane reflects what the recipient will actually see with the
  // known set of {{tokens}} resolved.
  const previewContext = useMemo(() => {
    const ctx: Record<string, string> = {};
    for (const v of meta.variables) ctx[v.key] = v.example || `{{${v.key}}}`;
    return ctx;
  }, [meta.variables]);
  const previewSubject = useMemo(() => renderWithSampleContext(subject, previewContext), [subject, previewContext]);
  const previewBody    = useMemo(() => renderWithSampleContext(body, previewContext), [body, previewContext]);
  const smsInfo        = useMemo(() => smsSegments(body), [body]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "min(1080px, 95vw)" },
          bgcolor: "#F7F9FC",
        },
      }}
    >
      {/* ── Header ────────────────────────────────────────────────── */}
      <Box sx={{
        px: { xs: 2, md: 3 }, py: 2, bgcolor: "#FFFFFF",
        borderBottom: `1px solid ${B13.line}`, display: "flex", alignItems: "center", gap: 1.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2, bgcolor: tint12(B13.primary),
          color: B13.primary, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {channel === "email" ? <EmailOutlinedIcon /> : <SmsOutlinedIcon />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...B13TypeScale.h3, letterSpacing: "-0.01em" }}>
            {initialRow ? "Edit template" : "New template"}
          </Typography>
          <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink2 }}>
            {channel === "email" ? "Email · reusable across compose and campaigns"
                                 : "SMS · reusable across staff and parent messaging"}
          </Typography>
        </Box>
        <Button
          startIcon={<AutoAwesomeOutlinedIcon />}
          onClick={() => setAiOpen(true)}
          sx={{
            textTransform: "none", fontWeight: 600, height: 36, px: 1.5,
            borderRadius: "10px", color: "#7c3aed",
            bgcolor: "#7c3aed14",
            "&:hover": { bgcolor: "#7c3aed26" },
          }}
        >
          Write with AI
        </Button>
        <IconButton onClick={onClose} aria-label="Close editor">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* ── Body: left form, right live preview ───────────────────── */}
      <Box sx={{
        flex: 1, display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
        gap: 2, p: { xs: 2, md: 3 }, overflow: "auto",
      }}>
        {/* ── FORM ─────────────────────────────────────────────── */}
        <Stack spacing={2}>
          <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 3, border: `1px solid ${B13.line}`, p: 2 }}>
            <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5 }}>
              Template details
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                label="Template name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={filterFieldSx}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  fullWidth
                  label="Template key (optional)"
                  helperText="Auto-generated from name if left blank"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  sx={filterFieldSx}
                />
                <TextField
                  size="small"
                  select
                  fullWidth
                  label="Type"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value as CommunicationTemplateType)}
                  sx={filterFieldSx}
                >
                  {meta.types.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  sx={filterFieldSx}
                >
                  <MenuItem value="">Uncategorised</MenuItem>
                  {categoryOptions.map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label="Audience / visibility"
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as CommunicationTemplateVisibility)}
                  sx={filterFieldSx}
                >
                  {meta.visibilities.map((v) => (
                    <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                size="small"
                fullWidth
                label="Description (internal)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={filterFieldSx}
                multiline
                minRows={2}
              />
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />}
                label={isActive ? "Active" : "Archived"}
              />
            </Stack>
          </Box>

          <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 3, border: `1px solid ${B13.line}`, p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6 }}>
                {channel === "email" ? "Email content" : "Message content"}
              </Typography>
            </Stack>

            {channel === "email" && (
              <Stack spacing={1.5} sx={{ mb: 1.5 }}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    size="small"
                    fullWidth
                    required
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    inputRef={subjectRef}
                    sx={filterFieldSx}
                  />
                  <Button
                    size="small"
                    onClick={(e) => openVarMenuFor(e.currentTarget, "subject")}
                    sx={{
                      position: "absolute", right: 6, top: 6, textTransform: "none",
                      fontSize: 11.5, height: 28, minWidth: 0, px: 1,
                      color: B13.primary, bgcolor: tint12(B13.primary),
                      "&:hover": { bgcolor: tint12(B13.primary) },
                    }}
                  >
                    {"{ }"} Variable
                  </Button>
                </Box>
              </Stack>
            )}

            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                required
                label={channel === "email" ? "Body" : "SMS body"}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                inputRef={bodyRef}
                multiline
                minRows={channel === "email" ? 10 : 5}
                sx={filterFieldSx}
              />
              <Button
                size="small"
                onClick={(e) => openVarMenuFor(e.currentTarget, "body")}
                sx={{
                  position: "absolute", right: 6, top: 6, textTransform: "none",
                  fontSize: 11.5, height: 28, minWidth: 0, px: 1,
                  color: B13.primary, bgcolor: tint12(B13.primary),
                  "&:hover": { bgcolor: tint12(B13.primary) },
                }}
              >
                {"{ }"} Variable
              </Button>
            </Box>

            {channel === "sms" && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={`${smsInfo.chars} / ${smsInfo.perSegment}`}
                  sx={{ height: 22, fontSize: 11, bgcolor: "#F1F5F9", color: B13Ink.ink2 }}
                />
                <Chip
                  size="small"
                  label={`${smsInfo.segments} SMS`}
                  sx={{
                    height: 22, fontSize: 11,
                    bgcolor: smsInfo.segments > 1 ? "#FEF3C7" : "#DCFCE7",
                    color:   smsInfo.segments > 1 ? "#92400E" : "#15803D",
                    fontWeight: 700,
                  }}
                />
                <Chip
                  size="small"
                  label={smsInfo.encoding === "unicode" ? "Unicode" : "GSM-7"}
                  sx={{ height: 22, fontSize: 11, bgcolor: "#F1F5F9", color: B13Ink.ink2 }}
                />
              </Stack>
            )}
          </Box>
        </Stack>

        {/* ── LIVE PREVIEW ─────────────────────────────────────── */}
        <Stack spacing={2}>
          <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 3, border: `1px solid ${B13.line}`, p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Live preview with sample data
              </Typography>
              {channel === "email" && (
                <Stack direction="row" spacing={0.5} sx={{ bgcolor: "#F1F5F9", borderRadius: "8px", p: 0.25 }}>
                  {(["desktop", "mobile"] as const).map((m) => {
                    const active = previewMode === m;
                    return (
                      <IconButton
                        key={m}
                        size="small"
                        onClick={() => setPreviewMode(m)}
                        aria-pressed={active}
                        sx={{
                          bgcolor: active ? "#FFFFFF" : "transparent",
                          color: active ? B13.primary : B13Ink.ink2,
                          borderRadius: "6px", width: 30, height: 26,
                        }}
                      >
                        {m === "desktop" ? <DesktopMacIcon fontSize="small" /> : <PhoneIphoneIcon fontSize="small" />}
                      </IconButton>
                    );
                  })}
                </Stack>
              )}
            </Stack>

            {channel === "email" ? (
              <Box
                sx={{
                  border: `1px solid ${B13.line}`, borderRadius: 2, bgcolor: "#FAFBFC",
                  mx: previewMode === "mobile" ? "auto" : 0,
                  width: previewMode === "mobile" ? 320 : "100%",
                  maxWidth: "100%", overflow: "hidden",
                }}
              >
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#FFFFFF", borderBottom: `1px solid ${B13.line}` }}>
                  <Typography sx={{ fontSize: 11, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>
                    Subject
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: B13.ink, mt: 0.25 }}>
                    {previewSubject || "(no subject)"}
                  </Typography>
                </Box>
                {/<[a-z][\s\S]*>/i.test(previewBody) ? (
                  <Box
                    sx={{ p: 2, minHeight: 240, fontSize: 13.5, lineHeight: 1.55, color: B13.ink }}
                    dangerouslySetInnerHTML={{ __html: previewBody }}
                  />
                ) : (
                  <Box sx={{ p: 2, minHeight: 240, fontSize: 13.5, lineHeight: 1.55, color: B13.ink, whiteSpace: "pre-wrap" }}>
                    {previewBody || "(no body)"}
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ mx: "auto", width: 320 }}>
                <Box
                  sx={{
                    borderRadius: 4, bgcolor: "#F1F5F9", p: 2, minHeight: 180,
                    position: "relative", border: `1px solid ${B13.line}`,
                  }}
                >
                  <Box sx={{
                    display: "inline-block", bgcolor: "#FFFFFF",
                    borderRadius: "16px", px: 1.5, py: 1,
                    fontSize: 13.5, lineHeight: 1.4,
                    boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
                    maxWidth: "100%", wordWrap: "break-word", whiteSpace: "pre-wrap",
                  }}>
                    {previewBody || "(empty message)"}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 3, border: `1px solid ${B13.line}`, p: 2 }}>
            <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6, mb: 1 }}>
              Available variables
            </Typography>
            <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink2, mb: 1.5 }}>
              Use the "{'{ }'} Variable" button in the subject or body to insert one at your cursor.
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75}>
              {meta.variables.slice(0, 24).map((v) => (
                <Chip
                  key={v.key}
                  label={`{{${v.key}}}`}
                  size="small"
                  sx={{
                    fontSize: 11, height: 22, bgcolor: "#F1F5F9",
                    color: B13Ink.ink2, fontFamily: "monospace",
                  }}
                />
              ))}
              {meta.variables.length > 24 && (
                <Chip
                  size="small"
                  label={`+${meta.variables.length - 24} more`}
                  sx={{ height: 22, fontSize: 11, bgcolor: "#E2E8F0" }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <Box sx={{
        px: { xs: 2, md: 3 }, py: 1.5, bgcolor: "#FFFFFF",
        borderTop: `1px solid ${B13.line}`,
        display: "flex", justifyContent: "flex-end", gap: 1,
      }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave || saving}
          onClick={handleSave}
          sx={{
            textTransform: "none", bgcolor: B13.primary,
            "&:hover": { bgcolor: B13.primary }, borderRadius: "10px",
            fontWeight: 600, px: 2.5,
          }}
        >
          {saving ? "Saving…" : initialRow ? "Save changes" : "Create template"}
        </Button>
      </Box>

      {/* ── Insert-variable menu ──────────────────────────────────── */}
      <Menu
        anchorEl={varMenu}
        open={Boolean(varMenu)}
        onClose={() => setVarMenu(null)}
        PaperProps={{ sx: { maxHeight: 380, minWidth: 260 } }}
      >
        {Object.entries(groupedVars).flatMap(([group, vars]) => [
          <ListSubheader key={`h-${group}`} sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6, lineHeight: 2, bgcolor: "#F8FAFC" }}>
            {group}
          </ListSubheader>,
          ...vars.map((v) => (
            <MenuItem key={v.key} onClick={() => insertVariable(v.key)} sx={{ fontSize: 13 }}>
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: B13.primary }}>
                  {`{{${v.key}}}`}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: B13Ink.ink2 }}>{v.label}</Typography>
              </Box>
            </MenuItem>
          )),
        ])}
      </Menu>

      {/* ── AI Panel ──────────────────────────────────────────────── */}
      {aiOpen && (
        <AIAssistantPanel
          channel={channel}
          category={category || null}
          categoryLabel={categoryOptions.find((c) => c.value === category)?.label || null}
          name={name}
          subject={channel === "email" ? subject : undefined}
          onClose={() => setAiOpen(false)}
          onInsert={handleAIInsert}
          generate={(input) => aiGenerateTemplateBody(input)}
        />
      )}
    </Drawer>
  );
};

export type EditorDrawerAIGenerate = (input: {
  category?: string | null;
  category_label?: string | null;
  name?: string;
  subject?: string;
  tone?: AITone;
  length?: AILength;
  purpose?: string;
}) => Promise<{ body: string; variables: string[] }>;

export default EditorDrawer;
